/**
 * Display vocabulary shared by every billing surface (settings, banner, admin),
 * so a status or a feature is spelled one way everywhere.
 */
export const SUBSCRIPTION_STATUS_LABEL: Record<string, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Payment failed',
  canceled: 'Canceled',
  unpaid: 'Unpaid',
  paused: 'Paused',
  incomplete: 'Incomplete',
  incomplete_expired: 'Expired',
};

export const statusLabel = (status: string) => SUBSCRIPTION_STATUS_LABEL[status] ?? status;

/** What an AI credit was spent on, as a person reads it. Keys are the `feature` the ledger records. */
export const AI_FEATURE_LABEL: Record<string, string> = {
  'sentiment-classification': 'Survey sentiment analysis',
  'editor-ai': 'Editor AI assistant',
  learnings: 'Experiment learnings',
  'results-ai': 'Results AI assistant',
};

export const aiFeatureLabel = (feature: string) => AI_FEATURE_LABEL[feature] ?? feature;

/** A date in the viewer's own locale (e.g. "Oct 8, 2026" or "8 okt. 2026"), never a raw ISO string. */
export const formatBillingDate = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString(undefined, { dateStyle: 'medium' });
};

/** Units as a whole number with the viewer's grouping. */
export const formatUnits = (units: number) => units.toLocaleString();

/** Whole days from now until `value`, floored at zero. Null without a date. */
export const daysUntil = (value: string | null, now: number) => {
  if (!value) return null;
  const ms = new Date(value).getTime() - now;
  return Number.isNaN(ms) ? null : Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
};

/** A series day (`YYYY-MM-DD`, a UTC day) as a short axis label in the viewer's locale, e.g. "Oct 3". */
export const formatSeriesDay = (day: string) => {
  const date = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? day
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const EURO = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

/** Whole euros from euro cents, in the viewer's locale (e.g. "€390" or "390 €"). Plan prices have no cents. */
export const formatEuro = (cents: number) => EURO.format(Math.round(cents / 100));
